import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import appActions from "@/redux/app/actions";
import companyActions from "@redux/company/actions";
import facilityActions from "@redux/facility/actions";
import {
  buildFacilityPath,
  buildPortfolioPath,
  extractContextFromPath,
  getAllowedPortfolioCompanyIds,
  isFacilityPath,
  isPortfolioPath,
} from "@/utils/route-context";

const FACILITY_VIEW_MODE = 0;
const PORTFOLIO_VIEW_MODE = 2;

const INTERNAL_ERROR_LEAFS = new Set(["403", "404"]);

const buildFacilityReducerShape = (facility) => ({
  Id: facility.id,
  key: facility.id,
  value: facility.name,
  label: facility.name,
  record: facility,
});

const buildFacilityOptions = (facilities = []) => {
  return facilities.map((facility) => buildFacilityReducerShape(facility));
};

const buildCompanyOptions = (facilities = []) => {
  const companyMap = new Map();

  facilities.forEach((facility) => {
    const companyId = facility.company_id;
    if (!companyMap.has(companyId)) {
      companyMap.set(companyId, {
        id: companyId,
        Id: companyId,
        label: facility.company,
        value: facility.company,
        facilities: [],
      });
    }
    companyMap.get(companyId).facilities.push(buildFacilityReducerShape(facility));
  });

  return Array.from(companyMap.values());
};

export const useRouteContextSync = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const authUserFacilities = useSelector((state) => state.Auth.user_facilities);
  const sessionResetting = useSelector((state) => state.Auth.sessionResetting);
  const selectedCompanyId = useSelector((state) => state.Company.selectedId);
  const selectedFacilityId = useSelector((state) => state.Facility.selected?.Id);
  const storedFacilityOptions = useSelector((state) => state.Facility.data);
  const storedCompanyOptions = useSelector((state) => state.Company.data);

  const context = useMemo(() => extractContextFromPath(location.pathname), [location.pathname]);
  const userFacilities = Array.isArray(authUserFacilities) ? authUserFacilities : [];
  const allowedPortfolioCompanyIds = useMemo(
    () => new Set(getAllowedPortfolioCompanyIds({ user_facilities: userFacilities })),
    [userFacilities]
  );
  const contextLeaf = useMemo(() => {
    if (!location.pathname) {
      return "";
    }

    if (isFacilityPath(location.pathname)) {
      const match = location.pathname.match(/^\/facility\/[^/]+\/[^/]+(?:\/([^/]+))?/);
      return match?.[1] || "";
    }

    if (isPortfolioPath(location.pathname)) {
      const match = location.pathname.match(/^\/portfolio\/[^/]+(?:\/([^/]+))?/);
      return match?.[1] || "";
    }

    return "";
  }, [location.pathname]);

  const isInternalErrorRoute = INTERNAL_ERROR_LEAFS.has(contextLeaf);
  const isRouteContextSynced = useMemo(() => {
    if (sessionResetting) {
      return false;
    }

    if (isInternalErrorRoute) {
      return true;
    }

    if (isFacilityPath(location.pathname)) {
      const companyId = context.companyId ? String(context.companyId) : null;
      const facilityId = context.facilityId ? String(context.facilityId) : null;

      if (!companyId || !facilityId) {
        return true;
      }

      if (!Array.isArray(authUserFacilities)) {
        return false;
      }

      const matchedFacility = userFacilities.some(
        (facility) => String(facility.id) === facilityId && String(facility.company_id) === companyId
      );

      if (!matchedFacility) {
        return true;
      }

      return String(selectedCompanyId || "") === companyId && String(selectedFacilityId || "") === facilityId;
    }

    if (isPortfolioPath(location.pathname)) {
      const companyId = context.companyId ? String(context.companyId) : null;

      if (!companyId) {
        return true;
      }

      if (!Array.isArray(authUserFacilities)) {
        return false;
      }

      if (!allowedPortfolioCompanyIds.has(companyId)) {
        return true;
      }

      return String(selectedCompanyId || "") === companyId;
    }

    return true;
  }, [
    allowedPortfolioCompanyIds,
    authUserFacilities,
    context.companyId,
    context.facilityId,
    isInternalErrorRoute,
    location.pathname,
    selectedCompanyId,
    selectedFacilityId,
    sessionResetting,
    userFacilities,
  ]);

  useEffect(() => {
    if (sessionResetting) {
      return;
    }

    if (Array.isArray(authUserFacilities) && userFacilities.length > 0) {
      if (!Array.isArray(storedFacilityOptions) || storedFacilityOptions.length === 0) {
        dispatch(facilityActions.FacilitySuccess(buildFacilityOptions(userFacilities)));
      }

      if (!Array.isArray(storedCompanyOptions) || storedCompanyOptions.length === 0) {
        dispatch(companyActions.CompanySuccess(buildCompanyOptions(userFacilities)));
      }
    }

    if (isFacilityPath(location.pathname)) {
      const companyId = context.companyId ? String(context.companyId) : null;
      const facilityId = context.facilityId ? String(context.facilityId) : null;

      if (!companyId || !facilityId) {
        return;
      }

      if (!Array.isArray(authUserFacilities)) {
        return;
      }

      const matchedFacility = userFacilities.find(
        (facility) => String(facility.id) === facilityId && String(facility.company_id) === companyId
      );

      if (!matchedFacility) {
        return;
      }

      dispatch(appActions.setViewMode(FACILITY_VIEW_MODE));

      if (String(selectedCompanyId || "") !== companyId) {
        dispatch(companyActions.setSelectedCompanyId(Number(companyId)));
      }

      if (String(selectedFacilityId || "") !== facilityId) {
        dispatch(facilityActions.SelectedFacility(buildFacilityReducerShape(matchedFacility)));
      }

      return;
    }

    if (isPortfolioPath(location.pathname)) {
      const companyId = context.companyId ? String(context.companyId) : null;

      if (!companyId) {
        return;
      }

      if (!Array.isArray(authUserFacilities)) {
        return;
      }

      if (!allowedPortfolioCompanyIds.has(companyId)) {
        return;
      }

      dispatch(appActions.setViewMode(PORTFOLIO_VIEW_MODE));

      if (String(selectedCompanyId || "") !== companyId) {
        dispatch(companyActions.setSelectedCompanyId(Number(companyId)));
      }
    }
  }, [
    authUserFacilities,
    context.companyId,
    context.facilityId,
    dispatch,
    location.pathname,
    navigate,
    selectedCompanyId,
    selectedFacilityId,
    sessionResetting,
    storedCompanyOptions,
    storedFacilityOptions,
    userFacilities,
    allowedPortfolioCompanyIds,
  ]);

  return isRouteContextSynced;
};

export const getDefaultFacilityPathFromState = (state) => {
  const userFacilities = Array.isArray(state?.Auth?.user_facilities) ? state.Auth.user_facilities : [];
  const selectedCompanyId = state?.Company?.selectedId;
  const selectedFacilityId = state?.Facility?.selected?.Id;

  const selectedFacility = userFacilities.find(
    (facility) =>
      String(facility.company_id) === String(selectedCompanyId || "") &&
      String(facility.id) === String(selectedFacilityId || "")
  );

  const fallbackFacility = selectedFacility || userFacilities[0];

  if (!fallbackFacility) {
    return null;
  }

  return buildFacilityPath("dashboard", {
    companyId: fallbackFacility.company_id,
    facilityId: fallbackFacility.id,
  });
};

export const getDefaultPortfolioPathFromState = (state) => {
  const allowedCompanyIds = getAllowedPortfolioCompanyIds(state?.Auth || {});
  const selectedCompanyId = state?.Company?.selectedId;
  const fallbackCompanyId = selectedCompanyId || allowedCompanyIds[0];

  if (!fallbackCompanyId) {
    return null;
  }

  return buildPortfolioPath("dashboard", {
    companyId: fallbackCompanyId,
  });
};
